import { useEffect, useState } from "react";
import { onMessage } from "firebase/messaging";
import { notification } from "antd";
import { t } from "i18next";
import { getFcmMessaging } from "services/firebase";
import {
  isPushEnabledInSettings,
  isPushSupported,
  PUSH_SETTING_EVENT,
  pushPermission,
  registerPushToken,
  unregisterPushToken,
} from "services/push-notifications";

/**
 * Keeps this browser's FCM registration in step with the user's choice: it
 * registers the token and listens for foreground messages once permission is
 * granted, and tears both down when notifications are switched off.
 *
 * It deliberately does NOT ask for permission. Firefox and Safari only prompt
 * when the call carries user activation, which a mount-time effect has none of,
 * and Chrome — the one browser that still prompts without it — penalises
 * drive-by requests. Asking belongs to the surfaces a user clicks: `PushOptIn`
 * on the notifications page, and the Settings panel toggle.
 *
 * @param {boolean} isAuthenticated Whether the current user is authenticated
 */
export const useFcmToken = (isAuthenticated) => {
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    const handleSettingChange = () => setTrigger((prev) => prev + 1);

    window.addEventListener(PUSH_SETTING_EVENT, handleSettingChange);
    return () =>
      window.removeEventListener(PUSH_SETTING_EVENT, handleSettingChange);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!isPushSupported()) {
      console.warn("Push notifications are not supported in this browser.");
      return;
    }

    let unsubscribe = null;
    let cancelled = false;

    const sync = async () => {
      try {
        if (!isPushEnabledInSettings()) {
          await unregisterPushToken();
          return;
        }

        // Nothing to do until someone has clicked through the prompt.
        if (pushPermission() !== "granted") return;

        await registerPushToken();

        const messaging = await getFcmMessaging();
        if (!messaging) return;

        const stop = onMessage(messaging, (payload) => {
          // Data-only payload, matching the mobile apps — see the service worker.
          notification.info({
            message:
              payload.notification?.title ||
              payload.data?.title ||
              t("notificationsCenter.title"),
            description:
              payload.notification?.body || payload.data?.message || "",
            placement: "topRight",
            duration: 5,
          });
        });

        if (cancelled) stop();
        else unsubscribe = stop;
      } catch (error) {
        console.error("An error occurred during push setup:", error);
      }
    };

    sync();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [isAuthenticated, trigger]);
};
